import React from 'react';
import { Toast, useToastState, useToastController } from '@tamagui/toast';
import { YStack, XStack } from '@tamagui/stacks';
import { Text } from '@tamagui/core';
import { CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react-native';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ShowToastOptions {
  title: string;
  message?: string;
  type?: ToastType;
  duration?: number;
}

let toastController: ReturnType<typeof useToastController> | null = null;

export const setToastController = (controller: ReturnType<typeof useToastController>) => {
  toastController = controller;
};

export const showToast = ({ title, message, type = 'info', duration = 3000 }: ShowToastOptions) => {
  if (!toastController) {
    console.warn('Toast controller not initialized');
    return;
  }

  toastController.show(title, {
    message,
    duration,
    native: false,
    viewportName: 'main',
    customData: { type },
  });
};

export const showSuccessToast = (title: string, message?: string) => {
  showToast({ title, message, type: 'success' });
};

export const showErrorToast = (title: string, message?: string) => {
  showToast({ title, message, type: 'error', duration: 4000 });
};

export const showWarningToast = (title: string, message?: string) => {
  showToast({ title, message, type: 'warning' });
};

export const showInfoToast = (title: string, message?: string) => {
  showToast({ title, message, type: 'info' });
};

const getIconForType = (type: ToastType) => {
  const iconProps = { size: 20, color: 'white' };
  
  switch (type) {
    case 'success':
      return <CheckCircle {...iconProps} />;
    case 'error':
      return <XCircle {...iconProps} />;
    case 'warning':
      return <AlertCircle {...iconProps} />;
    case 'info':
    default:
      return <Info {...iconProps} />;
  }
};

const getBackgroundForType = (type: ToastType) => {
  switch (type) {
    case 'success':
      return '$success';
    case 'error':
      return '$error';
    case 'warning':
      return '$warning';
    case 'info':
    default:
      return '$primary';
  }
};

export const CustomToast = () => {
  const currentToast = useToastState();

  if (!currentToast || currentToast.isHandledNatively) {
    return null;
  }

  const type = (currentToast.customData?.type as ToastType) || 'info';

  return (
    <Toast
      key={currentToast.id}
      duration={currentToast.duration}
      enterStyle={{ opacity: 0, scale: 0.9, y: -20 }}
      exitStyle={{ opacity: 0, scale: 0.9, y: -20 }}
      y={0}
      opacity={1}
      scale={1}
      animation="quick"
      viewportName="main"
    >
      <YStack
        backgroundColor={getBackgroundForType(type)}
        borderRadius="$4"
        paddingVertical="$3"
        paddingHorizontal="$4"
        minWidth={300}
        maxWidth="90%"
        shadowColor="$shadowColor"
        shadowOffset={{ width: 0, height: 2 }}
        shadowOpacity={0.25}
        shadowRadius={3.84}
        elevation={5}
      >
        <XStack space="$3" alignItems="center">
          {getIconForType(type)}
          <YStack flex={1}>
            <Toast.Title
              color="white"
              fontSize="$5"
              fontWeight="600"
              numberOfLines={2}
            >
              {currentToast.title}
            </Toast.Title>
            {currentToast.message && (
              <Toast.Description
                color="white"
                fontSize="$3"
                opacity={0.9}
                marginTop="$1"
                numberOfLines={3}
              >
                {currentToast.message}
              </Toast.Description>
            )}
          </YStack>
        </XStack>
      </YStack>
    </Toast>
  );
};

